<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Benutzerkonten</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('admin_users')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        id="filter-button"
                        :aria-label="$t('Filter menu')"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        type="button"
                        @click="toggleFiltersSidebar"
                    >
                        <span
                            v-if="hasActiveFilters"
                            class="stage-filter-active-dot"
                            aria-hidden="true"
                        ></span>
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        type="button"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        @click="getUsers"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>
                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div
                        :class="{'user-accounts-layout-empty': !routeHasEditor && !loading && userList.length === 0}"
                        class="user-accounts-layout h-full"
                    >
                        <section class="user-accounts-master flex min-h-0 flex-col" aria-label="Benutzerkontenliste">
                            <div class="user-accounts-toolbar">
                                <div class="user-accounts-view-switch" role="group" aria-label="Ansicht auswählen">
                                    <button
                                        :class="{'user-accounts-view-button-active': viewMode === 'list'}"
                                        aria-label="Listenansicht"
                                        class="user-accounts-view-button"
                                        title="Listenansicht"
                                        type="button"
                                        @click="setViewMode('list')"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.bars"/>
                                    </button>
                                    <button
                                        :class="{'user-accounts-view-button-active': viewMode === 'grid'}"
                                        aria-label="Kachelansicht"
                                        class="user-accounts-view-button"
                                        title="Kachelansicht"
                                        type="button"
                                        @click="setViewMode('grid')"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.grid-2"/>
                                    </button>
                                </div>
                                <button
                                    aria-label="Benutzerkonto anlegen"
                                    class="btn-stage-icon-plain user-accounts-create-button"
                                    title="Benutzerkonto anlegen"
                                    type="button"
                                    @click="beginCreate"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"/>
                                </button>
                            </div>
                            <div class="flex-1 min-h-0 overflow-auto">
                                <div
                                    v-if="userList.length > 0"
                                    :class="{'user-accounts-list-grid': viewMode === 'grid'}"
                                    class="user-accounts-list"
                                >
                                    <article
                                        v-for="user in userList"
                                        :key="'user-row-' + user.id"
                                        :class="{
                                            'user-account-entry-selected': Number(user.id) === Number(selectedUserId),
                                            'user-account-entry-card': viewMode === 'grid',
                                        }"
                                        class="user-account-entry"
                                        tabindex="0"
                                        @click="selectUser(user)"
                                        @keydown.enter.prevent="selectUser(user)"
                                        @keydown.space.prevent="selectUser(user)"
                                    >
                                        <div class="user-account-identity">
                                            <img
                                                :alt="user.name || 'Benutzerkonto'"
                                                :src="userAvatar(user)"
                                                class="user-account-avatar"
                                            >
                                            <div class="min-w-0">
                                                <div class="user-account-title-row">
                                                    <div class="user-account-primary truncate">{{ user.name || '–' }}</div>
                                                    <span class="user-account-role-badge">{{ getUserRoleLabel(user) }}</span>
                                                </div>
                                                <div class="user-account-secondary truncate">{{ user.email || 'Keine E-Mail-Adresse' }}</div>
                                            </div>
                                        </div>

                                        <div class="user-account-status">
                                            <span :class="user.status ? 'user-account-status-active' : 'user-account-status-inactive'">
                                                {{ user.status ? 'Aktiv' : 'Inaktiv' }}
                                            </span>
                                        </div>

                                        <div class="user-account-actions" @click.stop>
                                            <div class="relative inline-flex items-center justify-center">
                                                <button
                                                    :aria-expanded="Number(activeActionsUserId) === Number(user.id) ? 'true' : 'false'"
                                                    aria-haspopup="menu"
                                                    aria-label="Aktionen"
                                                    class="inline-flex h-7 w-7 items-center justify-center text-gray-500"
                                                    title="Aktionen"
                                                    type="button"
                                                    @click.stop.prevent="toggleUserActions(user.id)"
                                                >
                                                    <span class="flex flex-col items-center justify-center space-y-0.5">
                                                        <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                        <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                        <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                    </span>
                                                </button>
                                                <div
                                                    v-if="Number(activeActionsUserId) === Number(user.id)"
                                                    class="user-account-actions-menu absolute right-0 top-full z-30 mt-2 overflow-hidden rounded border border-bordergray-100 bg-white shadow-sm"
                                                    role="menu"
                                                >
                                                    <button
                                                        class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                        role="menuitem"
                                                        type="button"
                                                        @click.stop.prevent="navigateToEdit(user)"
                                                    >
                                                        {{ isCurrentUser(user) ? 'Eigenes Konto öffnen' : 'Benutzerkonto bearbeiten' }}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </article>
                                </div>

                                <div v-else-if="!loading" class="stage-empty-state">
                                    <div class="stage-empty-state-inner">
                                        <span class="stage-empty-state-icon-wrap">
                                            <svg-vue class="stage-empty-state-icon" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                        </span>
                                        <h3 class="stage-empty-state-title">Keine Benutzerkonten gefunden</h3>
                                    </div>
                                </div>
                            </div>

                            <list-footer-pagination
                                :start="paginationStart"
                                :end="paginationEnd"
                                :total="pagination.total"
                                :current-page="pagination.currentPage"
                                :total-pages="pagination.totalPages"
                                nav-class="user-accounts-pagination"
                                @prev="changePage(pagination.currentPage - 1)"
                                @next="changePage(pagination.currentPage + 1)"
                            />
                        </section>

                        <button
                            v-if="(routeHasEditor || selectedUser) && mobileDetailOpen"
                            aria-label="Detailansicht schließen"
                            class="user-account-detail-backdrop"
                            type="button"
                            @click="closeActiveDetail"
                        ></button>

                        <section
                            v-if="routeIsNew"
                            :class="{'user-account-detail-mobile-open': mobileDetailOpen}"
                            aria-label="Benutzerkonto anlegen"
                            class="user-account-detail min-h-0"
                        >
                            <workspace-admin-users-new-page
                                embedded
                                @close="closeCreate"
                                @saved="handleCreatedUser"
                            />
                        </section>

                        <section
                            v-else-if="routeIsEdit"
                            :class="{'user-account-detail-mobile-open': mobileDetailOpen}"
                            aria-label="Benutzerkonto bearbeiten"
                            class="user-account-detail min-h-0"
                        >
                            <workspace-admin-users-edit-page
                                :key="'embedded-user-edit-' + routeEditUserId"
                                embedded
                                @close="closeEdit"
                                @deleted="handleDeletedUser"
                                @updated="handleUpdatedUser"
                            />
                        </section>

                        <section
                            v-else-if="selectedUser"
                            :class="{'user-account-detail-mobile-open': mobileDetailOpen}"
                            aria-labelledby="user-account-detail-title"
                            class="user-account-detail min-h-0"
                        >
                            <div class="user-account-detail-body">
                            <div class="user-account-detail-toolbar">
                                <button
                                    aria-label="Detailansicht schließen"
                                    class="user-account-detail-close"
                                    title="Zurück"
                                    type="button"
                                    @click="closeMobileDetail"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-right"/>
                                </button>
                                <span class="user-account-detail-toolbar-title">Kontaktdetails</span>
                            </div>

                            <div class="user-account-detail-header">
                                <img
                                    :alt="selectedUser.name || 'Benutzerkonto'"
                                    :src="userAvatar(selectedUser)"
                                    class="user-account-detail-avatar"
                                >
                                <div class="min-w-0 flex-1">
                                    <div class="user-account-detail-title-row">
                                        <h2 id="user-account-detail-title" class="text-xl font-semibold leading-tight text-gray-900">
                                            {{ selectedUser.name || '–' }}
                                        </h2>
                                        <span class="user-account-role-badge">{{ getUserRoleLabel(selectedUser) }}</span>
                                    </div>
                                    <p class="mt-0.5 text-sm leading-4 text-gray-500">{{ selectedUser.email || 'Keine E-Mail-Adresse' }}</p>
                                </div>
                            </div>

                            <div class="user-account-detail-section-header">
                                <h3 class="user-account-detail-section-title">Benutzerkonto</h3>
                            </div>
                            <div class="user-account-detail-table" role="table">
                                <div class="user-account-detail-row" role="row">
                                    <div class="user-account-detail-label" role="rowheader">Status</div>
                                    <div class="user-account-detail-value" role="cell">
                                        <span :class="selectedUser.status ? 'user-account-status-active' : 'user-account-status-inactive'">
                                            {{ selectedUser.status ? 'Aktiv' : 'Inaktiv' }}
                                        </span>
                                    </div>
                                </div>
                                <div class="user-account-detail-row" role="row">
                                    <div class="user-account-detail-label" role="rowheader">E-Mail</div>
                                    <div class="user-account-detail-value" role="cell">
                                        <a v-if="selectedUser.email" :href="`mailto:${selectedUser.email}`" class="user-account-detail-link">
                                            {{ selectedUser.email }}
                                        </a>
                                        <span v-else>–</span>
                                    </div>
                                </div>
                                <div class="user-account-detail-row" role="row">
                                    <div class="user-account-detail-label" role="rowheader">Unternehmen</div>
                                    <div class="user-account-detail-value" role="cell">{{ userCompanyName(selectedUser) }}</div>
                                </div>
                                <div class="user-account-detail-row" role="row">
                                    <div class="user-account-detail-label" role="rowheader">Anmeldung</div>
                                    <div class="user-account-detail-value" role="cell">{{ loginMethodLabel(selectedUser) }}</div>
                                </div>
                            </div>

                            <div class="user-account-detail-section-header">
                                <h3 class="user-account-detail-section-title">Systemkomponenten</h3>
                            </div>
                            <div v-if="selectedUserModules.length > 0" class="user-account-module-list">
                                <span
                                    v-for="module in selectedUserModules"
                                    :key="module.key"
                                    class="user-account-module-badge"
                                >
                                    {{ module.label }}
                                </span>
                            </div>
                            <p v-else class="user-account-detail-empty-text">Keine Arbeitsbereiche freigeschaltet.</p>

                            </div>
                            <footer class="user-account-detail-footer">
                                <span v-if="selectedUser.updated_at" class="user-account-detail-updated">
                                <img
                                    :alt="updateActorName(selectedUser)"
                                    :src="updateActorAvatar(selectedUser)"
                                    class="user-account-detail-updated-avatar"
                                >
                                <span>{{ updateActorName(selectedUser) }}</span>
                                <span>am {{ formatUpdatedAt(selectedUser.updated_at) }}</span>
                                </span>
                                <button
                                    :aria-label="isCurrentUser(selectedUser) ? 'Eigenes Konto öffnen' : 'Benutzerkonto bearbeiten'"
                                    class="user-account-detail-edit"
                                    :title="isCurrentUser(selectedUser) ? 'Eigenes Konto öffnen' : 'Benutzerkonto bearbeiten'"
                                    type="button"
                                    @click="navigateToEdit(selectedUser)"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.pen-to-square"/>
                                </button>
                            </footer>
                        </section>

                        <section v-else-if="userList.length > 0" class="user-account-detail user-account-detail-empty">
                            <div class="user-account-detail-toolbar">
                                <span class="user-account-detail-toolbar-title">Kontaktdetails</span>
                            </div>
                            <div class="user-account-detail-empty-body">
                                <span class="stage-empty-state-icon-wrap">
                                    <svg-vue class="stage-empty-state-icon" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                </span>
                                <h2 class="stage-empty-state-title">Benutzerkonto auswählen</h2>
                            </div>
                        </section>
                    </div>

                    <aside
                        :aria-hidden="!filtersSidebar"
                        class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                        :class="filtersSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                    >
                        <div class="stage-filter-body">
                            <div class="stage-filter-title-row">
                                <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                                <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                            <div class="stage-filter-row mb-3">
                                <button class="stage-filter-sort-btn text-sm leading-5 font-medium" type="button" @click="changeSortOrder">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"/>
                                </button>
                                <div class="stage-filter-sort-select-wrap">
                                    <select
                                        v-model="sort.column"
                                        aria-label="Sort by"
                                        class="stage-filter-sort-select block form-select text-sm leading-5 font-medium"
                                        @change="applyFilters"
                                    >
                                        <option value="name">Name</option>
                                        <option value="email">E-Mail</option>
                                        <option value="role_id">{{ $t('Role') }}</option>
                                        <option value="status">Status</option>
                                        <option value="created_at">{{ $t('Created at') }}</option>
                                    </select>
                                    <span class="stage-filter-select-icon">
                                        <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                    </span>
                                </div>
                            </div>
                            <div class="stage-filter-label">{{ $t('Search') }}</div>
                            <div class="mb-4">
                                <div class="relative">
                                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                    </div>
                                    <input
                                        v-model.lazy="filters.search"
                                        :placeholder="$t('Search')"
                                        class="stage-filter-input form-input block pl-10 text-sm"
                                        @change="applyFilters"
                                    >
                                </div>
                            </div>
                            <div v-if="anyFilter">
                                <button
                                    type="button"
                                    class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                                    @click="resetFilters"
                                >
                                    {{ $t('Reset selection') }}
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>

            </div>
        </div>
    </div>
</template>

<script>

import {
    queryNumber,
    queryString,
    queryWithoutKeys,
    resolveReturnToPath,
    routeWithReturnTo,
    sameRouteQuery,
    setQueryValue,
} from "@/utils/list-route-state";
import WorkspaceAdminUsersNewPage from '@/views/pages/workspace/admin/users/new';
import WorkspaceAdminUsersEditPage from '@/views/pages/workspace/admin/users/edit';

export default {
    name: "dashboard-users-list",
    components: {WorkspaceAdminUsersEditPage, WorkspaceAdminUsersNewPage},
    metaInfo() {
        return {
            title: 'Benutzerkonten',
        };
    },
    data() {
        const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
        const allowedSortColumns = ['name', 'email', 'role_id', 'status', 'created_at'];
        const initialSortColumn = queryString(routeQuery, 'list_sort_column', 'created_at');
        const initialSortOrder = queryString(routeQuery, 'list_sort_order', 'desc') === 'asc' ? 'asc' : 'desc';

        return {
            page: queryNumber(routeQuery, 'list_page', 1),
            perPage: 20,
            loading: true,
            filtersSidebar: false,
            filters: {
                search: queryString(routeQuery, 'list_search', ''),
            },
            sort: {
                order: initialSortOrder,
                column: allowedSortColumns.includes(initialSortColumn) ? initialSortColumn : 'created_at',
            },
            pagination: {
                currentPage: 1,
                perPage: 20,
                total: 0,
                totalPages: 1,
            },
            userList: [],
            selectedUserId: null,
            mobileDetailOpen: /\/admin\/users\/(new|\d+\/edit)$/.test(String(this.$route && this.$route.path ? this.$route.path : '')),
            activeActionsUserId: null,
            viewMode: typeof window !== 'undefined' && window.localStorage.getItem('admin.users.view_mode') === 'grid'
                ? 'grid'
                : 'list',
        };
    },
    mounted() {
        document.addEventListener('click', this.closeUserActions);
        this.getUsers();
    },
    beforeDestroy() {
        document.removeEventListener('click', this.closeUserActions);
    },
    watch: {
        '$route.path'() {
            if (this.routeIsNew) {
                this.selectedUserId = null;
                this.mobileDetailOpen = true;
                return;
            }

            if (this.routeIsEdit) {
                this.selectedUserId = this.routeEditUserId;
                this.mobileDetailOpen = true;
                return;
            }

            if (!this.selectedUserId && this.userList.length > 0) {
                this.selectedUserId = Number(this.userList[0].id);
            }
        },
    },
    computed: {
        routeIsNew() {
            return String(this.$route && this.$route.path ? this.$route.path : '').endsWith('/admin/users/new');
        },
        routeEditUserId() {
            if (!this.$route || !this.$route.params || !this.$route.params.id) {
                return null;
            }

            const userId = Number(this.$route.params.id);
            return Number.isInteger(userId) && userId > 0 ? userId : null;
        },
        routeIsEdit() {
            return Boolean(this.routeEditUserId)
                && String(this.$route && this.$route.path ? this.$route.path : '').endsWith('/edit');
        },
        routeHasEditor() {
            return this.routeIsNew || this.routeIsEdit;
        },
        selectedUser() {
            return this.userList.find((user) => Number(user.id) === Number(this.selectedUserId)) || null;
        },
        selectedUserModules() {
            if (!this.selectedUser || !this.selectedUser.access || !this.selectedUser.access.modules) {
                return [];
            }

            const modules = this.selectedUser.access.modules;
            const labels = [
                ['tickets', 'Tickets'],
                ['projects', 'Projekte'],
                ['relationships', 'Beziehungen'],
                ['boards', 'Boards'],
                ['positions', 'Positionen'],
                ['timeline', 'Timeline'],
                ['file_manager', 'Dateimanager'],
                ['documentation', 'Dokumentation'],
                ['finance', 'Finanzen'],
                ['vault', 'Sicherheitsnotizen'],
                ['server_monitoring', 'Systemerweiterungen'],
                ['cms', 'CMS'],
            ];

            return labels
                .filter(([key]) => Boolean(modules[key]))
                .map(([key, label]) => ({key, label}));
        },
        anyFilter() {
            return this.hasActiveFilters;
        },
        hasActiveFilters() {
            return (this.filters.search || '').trim() !== ''
                || this.sort.column !== 'created_at'
                || this.sort.order !== 'desc';
        },
        paginationStart() {
            if (this.pagination.total < 1) {
                return 0;
            }

            return ((this.pagination.currentPage - 1) * this.pagination.perPage) + 1;
        },
        paginationEnd() {
            if (this.pagination.total < 1) {
                return 0;
            }

            const pageEnd = this.pagination.currentPage * this.pagination.perPage;
            return pageEnd > this.pagination.total ? this.pagination.total : pageEnd;
        },
    },
    methods: {
        toggleFiltersSidebar() {
            this.filtersSidebar = !this.filtersSidebar;
        },
        isCurrentUser(user) {
            return Boolean(user && Number(user.id) === Number(this.$store.state.user.id));
        },
        selectUser(user) {
            if (!user || !user.id) {
                return;
            }

            this.selectedUserId = Number(user.id);
            this.mobileDetailOpen = true;
            this.closeUserActions();

            if (this.routeHasEditor) {
                this.$router.replace({path: '/workspace/admin/users', query: this.$route.query || {}}).catch(() => {});
            }
        },
        setViewMode(mode) {
            if (!['list', 'grid'].includes(mode)) {
                return;
            }

            this.viewMode = mode;
            if (typeof window !== 'undefined') {
                window.localStorage.setItem('admin.users.view_mode', mode);
            }
        },
        beginCreate() {
            this.selectedUserId = null;
            this.mobileDetailOpen = true;
            this.closeUserActions();

            if (!this.routeIsNew) {
                this.$router.push({path: '/workspace/admin/users/new', query: this.$route.query || {}}).catch(() => {});
            }
        },
        closeCreate() {
            this.mobileDetailOpen = false;
            this.$router.replace({path: '/workspace/admin/users', query: this.$route.query || {}}).catch(() => {});

            if (!this.selectedUserId && this.userList.length > 0) {
                this.selectedUserId = Number(this.userList[0].id);
            }
        },
        closeEdit() {
            const editedUserId = this.routeEditUserId;
            this.mobileDetailOpen = false;

            const routeQuery = this.$route && this.$route.query ? this.$route.query : {};
            const rawReturnTo = Array.isArray(routeQuery.return_to)
                ? routeQuery.return_to[0]
                : routeQuery.return_to;
            if (typeof rawReturnTo === 'string' && rawReturnTo.trim() !== '') {
                const relationshipsListPath = resolveReturnToPath(routeQuery, '/workspace/relationships');
                if (relationshipsListPath !== '/workspace/relationships' || rawReturnTo === '/workspace/relationships') {
                    this.$router.replace(relationshipsListPath).catch(() => {});
                    return;
                }
            }

            this.$router.replace({path: '/workspace/admin/users', query: this.$route.query || {}}).catch(() => {});

            if (editedUserId && this.userList.some((user) => Number(user.id) === editedUserId)) {
                this.selectedUserId = editedUserId;
            } else if (this.userList.length > 0) {
                this.selectedUserId = Number(this.userList[0].id);
            }
        },
        handleCreatedUser(user) {
            const createdUserId = user && user.id ? Number(user.id) : null;
            this.$router.replace({path: '/workspace/admin/users', query: this.$route.query || {}})
                .catch(() => {})
                .then(() => this.getUsers())
                .then(() => {
                    if (createdUserId && this.userList.some((item) => Number(item.id) === createdUserId)) {
                        this.selectedUserId = createdUserId;
                    }
                });
        },
        handleUpdatedUser(user) {
            if (!user || !user.id) {
                return;
            }

            const userIndex = this.userList.findIndex((item) => Number(item.id) === Number(user.id));
            if (userIndex >= 0) {
                const updatedUser = {...this.userList[userIndex], ...user};
                this.userList.splice(userIndex, 1, updatedUser);
            }
        },
        handleDeletedUser() {
            this.mobileDetailOpen = false;
            this.$router.replace({path: '/workspace/admin/users', query: this.$route.query || {}})
                .catch(() => {})
                .then(() => this.getUsers());
        },
        closeMobileDetail() {
            this.mobileDetailOpen = false;
        },
        closeActiveDetail() {
            if (this.routeIsNew) {
                this.closeCreate();
                return;
            }

            if (this.routeIsEdit) {
                this.closeEdit();
                return;
            }

            this.closeMobileDetail();
        },
        toggleUserActions(userId) {
            this.activeActionsUserId = Number(this.activeActionsUserId) === Number(userId)
                ? null
                : Number(userId);
        },
        closeUserActions() {
            this.activeActionsUserId = null;
        },
        getUserRoleLabel(user) {
            if (!user) {
                return '-';
            }

            if (user.role_name) {
                return user.role_name;
            }

            if (user.role_slug) {
                return user.role_slug;
            }

            return '-';
        },
        userAvatar(user) {
            if (user && user.avatar && user.avatar !== 'gravatar') {
                return user.avatar;
            }
            if (user && user.gravatar) {
                return user.gravatar;
            }

            return '/images/default/icon.png';
        },
        updateActorAvatar(user) {
            const actor = user && user.updated_by ? user.updated_by : user;
            return this.userAvatar(actor);
        },
        updateActorName(user) {
            if (user && user.updated_by && user.updated_by.name) {
                return user.updated_by.name;
            }
            return (user && user.name) ? user.name : 'Benutzerkonto';
        },
        userCompanyName(user) {
            if (user && user.company && user.company.name) {
                return user.company.name;
            }

            return '–';
        },
        loginMethodLabel(user) {
            const method = user && (user.effective_auth_login_method || user.auth_login_method);

            if (method === 'password') {
                return 'Passwort';
            }
            if (method === 'one_time_code') {
                return 'Einmalcode per E-Mail';
            }

            return method || '–';
        },
        formatRelativeDate(value) {
            if (!value) {
                return '-';
            }

            return moment(value).locale(window.app.app_date_locale).fromNow();
        },
        formatUpdatedAt(value) {
            if (!value) {
                return '–';
            }

            return this.$momentFormatDateTime(value).replace(/\s+(\d{1,2}:\d{2})$/, ', $1');
        },
        changePage(page) {
            if ((page > 0) && (page <= this.pagination.totalPages) && (page !== this.page)) {
                this.page = page;
                this.getUsers();
            }
        },
        changeSortOrder() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.applyFilters();
        },
        applyFilters() {
            this.page = 1;
            this.getUsers();
        },
        resetFilters() {
            this.filters.search = '';
            this.sort = {
                order: 'desc',
                column: 'created_at',
            };
            this.page = 1;
            this.getUsers();
        },
        getUsers() {
            const self = this;
            self.syncUsersListRouteState();
            self.loading = true;
            return axios.get('api/workspace/admin/users', {
                params: {
                    page: self.page,
                    sort: self.sort,
                    search: self.filters.search,
                    perPage: self.perPage,
                },
            }).then(function (response) {
                self.userList = Array.isArray(response.data.items) ? response.data.items : [];
                const apiPagination = response.data.pagination || {};
                self.pagination = {
                    currentPage: Number(apiPagination.currentPage || self.page || 1),
                    perPage: Number(apiPagination.perPage || self.perPage || 20),
                    total: Number(apiPagination.total || 0),
                    totalPages: Number(apiPagination.totalPages || 1),
                };

                if (self.pagination.totalPages > 0 && self.page > self.pagination.totalPages) {
                    self.page = self.pagination.totalPages;
                    self.getUsers();
                    return;
                }

                if (self.routeIsEdit) {
                    self.selectedUserId = self.routeEditUserId;
                    self.mobileDetailOpen = true;
                } else if (!self.userList.some((user) => Number(user.id) === Number(self.selectedUserId))) {
                    self.selectedUserId = self.userList.length > 0 ? Number(self.userList[0].id) : null;
                    self.mobileDetailOpen = self.routeHasEditor;
                }

                self.loading = false;
            }).catch(function () {
                self.loading = false;
            });
        },
        syncUsersListRouteState() {
            if (!this.$router || !this.$route) {
                return;
            }

            const removeKeys = [
                'list_page',
                'list_per_page',
                'list_sort_column',
                'list_sort_order',
                'list_search',
            ];

            let nextQuery = queryWithoutKeys(this.$route.query || {}, removeKeys);
            setQueryValue(nextQuery, 'list_page', Number(this.page || 1) === 1 ? null : Number(this.page || 1));
            setQueryValue(nextQuery, 'list_sort_column', this.sort.column === 'created_at' ? null : this.sort.column);
            setQueryValue(nextQuery, 'list_sort_order', this.sort.order === 'desc' ? null : this.sort.order);
            setQueryValue(nextQuery, 'list_search', (this.filters.search || '').trim() || null);

            if (!sameRouteQuery(this.$route.query || {}, nextQuery)) {
                this.$router.replace({path: this.$route.path, query: nextQuery}).catch(() => {});
            }
        },
        editPath(userId) {
            if (Number(userId) === Number(this.$store.state.user.id)) {
                return '/account';
            }

            return routeWithReturnTo(
                '/workspace/admin/users/' + userId + '/edit',
                this.$route,
                '/workspace/admin/users'
            );
        },
        navigateToEdit(user) {
            if (!user || !user.id) {
                return;
            }

            this.closeUserActions();
            this.$router.push(this.editPath(user.id));
        },
    },
};
</script>
<style scoped>
.user-accounts-layout {
    display: grid;
    grid-template-columns: minmax(34rem, 3fr) minmax(25rem, 2fr);
    height: 100%;
    min-height: 0;
    overflow: hidden;
}

.user-accounts-layout-empty {
    grid-template-columns: minmax(0, 1fr);
}

.user-accounts-master {
    border-right: 1px solid var(--border-ultralight);
}

.user-accounts-toolbar {
    display: flex;
    min-height: 3.25rem;
    flex: 0 0 3.25rem;
    align-items: stretch;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
}

.user-accounts-view-switch {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0 1rem;
}

.user-accounts-view-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: var(--ui-text-disabled);
    cursor: pointer;
}

.user-accounts-view-button {
    width: 2rem;
    height: 2rem;
}

.user-accounts-view-button-active,
.user-accounts-view-button:hover {
    color: var(--ui-text-secondary);
}

.user-accounts-view-button:focus {
    outline: none;
}

.user-accounts-create-button {
    width: 3.25rem;
    min-width: 3.25rem;
    margin-left: auto;
}

.user-accounts-list {
    min-height: 100%;
}

.user-accounts-list-grid {
    display: grid;
    min-height: 0;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 19rem), 1fr));
    align-content: start;
    gap: 1.25rem;
    padding: 1.25rem;
}

.user-accounts-pagination {
    background-color: var(--ui-surface-raised) !important;
}

.user-account-entry {
    position: relative;
    display: grid;
    min-height: 5.5rem;
    grid-template-columns: minmax(18rem, 1fr) minmax(6.5rem, 0.25fr) 1.5rem;
    align-items: center;
    gap: 1.5rem;
    padding: 0.9rem 1.5rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: #fff;
    cursor: pointer;
}

.user-account-entry:nth-child(even) {
    background: var(--ui-surface-raised);
}

.user-account-entry-card,
.user-account-entry-card:nth-child(even) {
    min-height: 8rem;
    grid-template-columns: minmax(0, 1fr) 1.5rem;
    grid-template-rows: minmax(0, 1fr) auto;
    gap: 0.75rem;
    padding: 1rem;
    overflow: visible;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
}

.user-account-entry-card .user-account-identity {
    grid-column: 1 / -1;
}

.user-account-entry-card .user-account-status {
    grid-column: 1;
}

.user-account-entry-card .user-account-actions {
    grid-column: 2;
}

.user-account-entry-selected::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: var(--ui-text-secondary);
    content: '';
    pointer-events: none;
}

.user-account-entry:focus {
    outline: none;
}

.user-account-identity,
.user-account-title-row,
.user-account-detail-title-row {
    display: flex;
    min-width: 0;
    align-items: center;
}

.user-account-identity {
    gap: 0.85rem;
}

.user-account-title-row,
.user-account-detail-title-row {
    gap: 0.5rem;
}

.user-account-detail-title-row {
    flex-wrap: wrap;
}

.user-account-avatar {
    width: 2.5rem;
    height: 2.5rem;
    flex: 0 0 2.5rem;
    border-radius: 9999px;
    object-fit: cover;
}

.user-account-primary {
    color: var(--ui-text-secondary);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.user-account-role-badge,
.user-account-module-badge {
    display: inline-flex;
    min-height: 1.35rem;
    flex: 0 0 auto;
    align-items: center;
    padding: 0.1rem 0.45rem;
    border: 1px solid var(--ui-border-default);
    border-radius: 0.25rem;
    background: #fff;
    color: var(--ui-text-muted);
    font-size: 0.6875rem;
    font-weight: 500;
    line-height: 1rem;
    white-space: nowrap;
}

.user-account-secondary {
    margin-top: 0.2rem;
    color: var(--ui-text-disabled);
    font-size: 0.75rem;
    line-height: 1rem;
}

.user-account-status {
    color: var(--ui-text-subtle);
    font-size: 0.875rem;
    line-height: 1.25rem;
    white-space: nowrap;
}

.user-account-status-active,
.user-account-status-inactive {
    display: inline-flex;
    align-items: center;
}

.user-account-status-inactive {
    padding: 0.25rem 0.75rem;
    border: 1px solid #fecaca;
    border-radius: 9999px;
    background: #fef2f2;
    color: #b91c1c;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1rem;
}

.user-account-actions {
    align-self: stretch;
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
}

.user-account-actions-menu {
    width: max-content;
    white-space: nowrap;
    text-transform: none;
    letter-spacing: normal;
}

.user-account-detail-backdrop {
    display: none;
}

.user-account-detail {
    display: flex;
    min-width: 0;
    height: 100%;
    max-height: 100%;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
}

.user-account-detail-body {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1.5rem 1.75rem 2rem;
}

.user-account-detail-toolbar {
    position: sticky;
    top: -1.5rem;
    z-index: 10;
    display: flex;
    min-height: 3.25rem;
    align-items: center;
    gap: 0.75rem;
    margin: -1.5rem -1.75rem 1.5rem;
    padding: 0 1rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
}

.user-account-detail-toolbar-title {
    min-width: 0;
    color: var(--ui-text-strong);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.user-account-detail-close,
.user-account-detail-edit {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: var(--ui-text-subtle);
    cursor: pointer;
}

.user-account-detail-close {
    display: none;
}

.user-account-detail-edit {
    margin-left: auto;
}

.user-account-detail-close:hover,
.user-account-detail-edit:hover {
    color: var(--ui-text-secondary);
}

.user-account-detail-close:focus,
.user-account-detail-edit:focus {
    outline: none;
}

.user-account-detail-header {
    display: flex;
    min-height: 4.75rem;
    align-items: flex-start;
    gap: 1rem;
}

.user-account-detail-avatar {
    width: 4.5rem;
    height: 4.5rem;
    flex: 0 0 4.5rem;
    border-radius: 9999px;
    object-fit: cover;
}

.user-account-detail-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-top: 1.5rem;
}

.user-account-detail-section-title {
    color: var(--ui-text-strong);
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
}

.user-account-detail-table {
    margin-top: 0.75rem;
    overflow: hidden;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
}

.user-account-detail-row {
    display: grid;
    grid-template-columns: 7rem minmax(0, 1fr);
    align-items: center;
    border-bottom: 1px solid var(--border-ultralight);
}

.user-account-detail-row:nth-child(even) {
    background: var(--ui-surface-raised);
}

.user-account-detail-row:last-child {
    border-bottom: 0;
}

.user-account-detail-label,
.user-account-detail-value {
    padding: 0.65rem 1rem;
    text-align: left;
}

.user-account-detail-label {
    color: var(--ui-text-subtle);
    font-size: 0.75rem;
    line-height: 1rem;
}

.user-account-detail-value {
    min-width: 0;
    overflow-wrap: anywhere;
    color: var(--ui-text-secondary);
    font-size: 0.875rem;
    line-height: 1.25rem;
}

.user-account-detail-link {
    color: inherit;
    text-decoration: none;
}

.user-account-detail-link:hover,
.user-account-detail-link:focus {
    color: var(--ui-text-strong);
    text-decoration: underline;
    text-underline-offset: 2px;
}

.user-account-module-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-top: 0.75rem;
    padding: 0.75rem;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: var(--ui-surface-raised);
}

.user-account-detail-empty-text {
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    color: var(--ui-text-subtle);
    font-size: 0.875rem;
}

.user-account-detail-footer {
    display: flex;
    height: 3.5rem;
    min-height: 3.5rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 0 1.5rem;
    background: var(--ui-surface-raised);
    box-shadow: inset 0 1px 0 var(--border-ultralight);
}

.user-account-detail-updated {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 0.375rem;
    overflow: hidden;
    color: var(--ui-text-subtle);
    font-size: 0.75rem;
    line-height: 1rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-account-detail-updated-avatar {
    width: 1.25rem;
    height: 1.25rem;
    flex: 0 0 1.25rem;
    border-radius: 9999px;
    object-fit: cover;
}

.user-account-detail-empty {
    align-items: stretch;
    justify-content: flex-start;
    text-align: center;
}

.user-account-detail-empty-body {
    display: flex;
    min-height: 0;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

@media (max-width: 1279px) {
    .user-accounts-layout {
        position: relative;
        display: block;
        height: 100%;
        min-height: 0;
        overflow: hidden;
    }

    .user-accounts-master {
        height: 100%;
        min-height: 0;
        border-right: 0;
    }

    .user-account-detail-backdrop {
        position: absolute;
        inset: 0;
        z-index: 20;
        display: block;
        width: 100%;
        height: 100%;
        padding: 0;
        border: 0;
        background: rgb(17 24 39 / 8%);
        cursor: pointer;
    }

    .user-account-detail {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 30;
        width: 92%;
        height: 100%;
        max-height: none;
        padding: 0;
        border-left: 1px solid var(--border-ultralight);
        box-shadow: -0.5rem 0 1.5rem rgb(0 0 0 / 12%);
        visibility: hidden;
        transform: translateX(100%);
        transition: transform 180ms ease-out, visibility 0s linear 180ms;
    }

    .user-account-detail-body {
        padding: 1.25rem 1rem 1.75rem;
    }

    .user-account-detail.user-account-detail-mobile-open {
        visibility: visible;
        transform: translateX(0);
        transition-delay: 0s;
    }

    .user-account-detail-toolbar {
        top: -1.25rem;
        margin: -1.25rem -1rem 1.25rem;
    }

    .user-account-detail-close {
        display: inline-flex;
    }
}

@media (max-width: 639px) {
    .user-account-entry {
        grid-template-columns: minmax(0, 1fr) 1.5rem;
        gap: 0.75rem;
        padding: 0.9rem 1rem;
    }

    .user-account-status {
        display: none;
    }

    .user-account-title-row {
        align-items: flex-start;
        flex-direction: column;
        gap: 0.15rem;
    }

    .user-account-detail-row {
        grid-template-columns: 6rem minmax(0, 1fr);
    }
}
</style>
